import React, { useState } from 'react';
import { Alert } from '../../../components/Alert';
import { Button, NakedButton } from '../../../components/Button';
import {
    Container,
    ColContainer,
    CardContainer,
    TPL_50_50,
    TPL_THIRDS,
} from '../../../components/Container';
import { FlashMessage } from '../../../components/FlashMessage';
import {
    GraphqlErrorMessage,
    ValidationErrorMessage,
} from '../../../components/ErrorMessage';
import { H1, H2, Byline } from '../../../components/Typography';
import { Helmet } from 'react-helmet';
import { Input } from '../../../components/Input';
import { SelectInput } from '../../../components/SelectInput';
import { Left, Right } from '../../../components/Header';
import { Loading, LoadingError } from '../../../components/LoadingFork';
import { StatusPill } from '../../../components/StatusPill';
import { css } from 'glamor';
import { fmtDate } from '../../../lib/misc';
import { useFormState } from 'react-use-form-state';
import { useQuery, useMutation } from '@apollo/client';
import { withInstallation } from '../../../components/InstallationContext';
import authManager, {PERMISSIONS} from '../../../lib/authManager';

import {
    userById,
    rolesQuery,
    updateUserMutation,
    updateUserPasswordMutation,
} from './queries';

import {
    hideGlobalLoader,
    showGlobalLoader,
} from '../../../lib/loaderController';

function Header(props) {
    return (
      <div
        className={css({
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
        })}
        {...props}
      />
    );
}

function Status({ isEnabled }) {
    return (
      <StatusPill flavour={isEnabled ? 'success' : 'light'}>
          {isEnabled ? 'Enabled' : 'Disabled'}
      </StatusPill>
    );
}

function Role({ role }) {
    return <span>{role?.label || role?.name || 'No role'}</span>;
}

function currentUserRoleAt(user, installationId) {
    return (
      user.userRoles?.find((userRole) => {
          return userRole.installationId === installationId;
      })?.role || null
    );
}

function UserDetailsForm({
     user,
     roles,
     onUpdate,
     onUpdatePassword,
     canChangeRole,
     canChangeMfa,
 }) {
    const roleOptions = roles.map((role) => ({
        value: role.id,
        label: role.label || role.name,
    }));

    const [
        {
            errors,
            values,
            reset,
        },
        { text, email, tel, select },
    ] = useFormState({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        isEnabled: user.isEnabled ? 'yes' : 'no',
        mfaEnabled: user.mfaEnabled ? 'yes' : 'no',
        roleId: user.roleId || '',
    });

    const [
        {
            errors: pErrors,
            values: pValues,
            reset: pReset,
        },
        { password: passwordInput },
    ] = useFormState({
        password: '',
        passwordConfirm: '',
    });

    const firstError = Object.values({ ...errors, ...pErrors })[0];
    const hasErrors = !!firstError;

    const isUpdatedDetails =
      user.name !== values.name ||
      user.email !== values.email ||
      user.phone !== values.phone ||
      user.isEnabled !== (values.isEnabled === 'yes') ||
      (canChangeMfa && user.mfaEnabled !== (values.mfaEnabled === 'yes')) ||
      user.roleId !== values.roleId;

    const isUpdatedPassword =
      pValues.password !== '' || pValues.passwordConfirm !== '';

    const canUpdatePassword =
      !hasErrors &&
      isUpdatedPassword &&
      pValues.password === pValues.passwordConfirm;

    async function onSubmit() {
        if (!isUpdatedDetails) return;

        await onUpdate(values);
    }

    async function onSubmitPassword() {
        if (!isUpdatedPassword) return;

        await onUpdatePassword(pValues.password);
        pReset();
    }

    function validatePassword(value, state) {
        if (state.password === state.passwordConfirm) return;

        return 'Passwords do not match';
    }

    return (
      <div>
          {hasErrors && <ValidationErrorMessage error={firstError} />}

          <ColContainer columnTemplate={TPL_THIRDS} spaceBelow="medium">
              <Input
                required
                label="Name"
                error={errors.name}
                {...text('name')}
              />

              {canChangeRole && (
                <SelectInput
                  required
                  label="Role"
                  error={errors.roleId}
                  options={roleOptions}
                  {...select('roleId')}
                />
              )}
              {canChangeMfa && <SelectInput label="Email MFA" options={[{ value: 'yes', label: 'Enabled' }, { value: 'no', label: 'Disabled' }]} {...select('mfaEnabled')} />}

              <Input
                required
                label="Email address"
                error={errors.email}
                {...email('email')}
              />
          </ColContainer>

          <ColContainer columnTemplate={TPL_THIRDS} spaceBelow="medium">
              <Input
                label="Contact number"
                error={errors.phone}
                {...tel('phone')}
              />
          </ColContainer>

          <Container spaceAbove="small">
              <Button
                type="submit"
                disabled={!isUpdatedDetails || hasErrors}
                style={{
                    backgroundColor: '#3127B8',
                    cursor: 'pointer',
                }}
                onClick={onSubmit}
              >
                  Update profile
              </Button>

              {isUpdatedDetails ? (
                <NakedButton
                  type="reset"
                  style={{ cursor: 'pointer' }}
                  flavour="gray"
                  onClick={() => reset()}
                >
                    Cancel
                </NakedButton>
              ) : null}
          </Container>

          <br />

          <Container spaceAbove="small">
              <H2>Update password</H2>
          </Container>

          <ColContainer columnTemplate={TPL_50_50}>
            {/* This helps password managers know which account password has been changed. */}
            <input name="email" defaultValue={values.email} hidden />

            <Input
              label="New Password"
              placeholder="New Password"
              autoComplete="new-password"
              error={pErrors.password}
              {...passwordInput({
                name: 'password',
              })}
            />

            <Input
              label="Confirm Password"
              placeholder="Confirm Password"
              autoComplete="new-password"
              error={pErrors.passwordConfirm}
              {...passwordInput({
                name: 'passwordConfirm',
                validate: validatePassword,
                validateOnBlur: true,
              })}
            />
          </ColContainer>

          <Container spaceAbove="small">
              <Button
                type="submit"
                disabled={!canUpdatePassword}
                onClick={onSubmitPassword}
              >
                  Update password
              </Button>

              {isUpdatedPassword ? (
                <NakedButton
                  type="reset"
                  flavour="gray"
                  onClick={() => pReset()}
                >
                    Cancel
                </NakedButton>
              ) : null}
          </Container>
      </div>
    );
}

function UserDetails({ installation, match, location, history }) {
    const { userId } = match.params;
    const [showSuccessMessage, setSuccessMessage] = useState(false);

    const [updateUser, { error: updateError }] = useMutation(updateUserMutation);
    const [updateUserPassword, { error: updatePasswordError }] = useMutation(
      updateUserPasswordMutation
    );

    const submitError = updateError || updatePasswordError;

    const {
        data = {},
        loading,
        error,
        refetch,
    } = useQuery(userById, {
        variables: {
            id: userId,
        },
        skip: !userId,
        fetchPolicy: 'no-cache',
    });

    const {
        data: rolesData = {},
        loading: rolesLoading,
        error: rolesError,
    } = useQuery(rolesQuery, {
        variables: {
            installationId: installation.id,
        },
        fetchPolicy: 'no-cache',
    });

    function onCancel() {
        history.goBack();
    }

    if (loading || rolesLoading) {
        return (
          <CardContainer>
              <Loading />
          </CardContainer>
        );
    }

    if (error) {
        return (
          <CardContainer>
              <LoadingError error={error} />
          </CardContainer>
        );
    }

    if (rolesError) {
        return (
          <CardContainer>
              <LoadingError error={rolesError} />
          </CardContainer>
        );
    }

    const user = data.user ? { ...data.user } : null;
    const roles = rolesData.roles || [];

    if (!user) {
        return (
          <CardContainer>
              <Container spaceAbove="medium" spaceBelow="medium">
                  <H1>Not found</H1>
                  <Button onClick={onCancel}>Back to Users</Button>
              </Container>
          </CardContainer>
        );
    }

    const currentRole = currentUserRoleAt(user, installation.id);
    user.roleId = currentRole?.id || '';

    async function onUpdate(values) {
        try {
            showGlobalLoader();

            await updateUser({
                variables: {
                    id: user.id,
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                    isEnabled: values.isEnabled === 'yes',
                    ...(authManager.isRootAdmin() ? { mfaEnabled: values.mfaEnabled === 'yes' } : {}),
                    roleId: values.roleId,
                },
            });

            setSuccessMessage(true);
            refetch();
        } catch (error) {
            console.error('Failed to update user:', error);
        } finally {
            hideGlobalLoader();
        }
    }

    async function onUpdatePassword(newPassword) {
        try {
            showGlobalLoader();

            await updateUserPassword({
                variables: {
                    id: user.id,
                    password: newPassword,
                },
            });

            setSuccessMessage(true);
        } catch (error) {
            console.error('Failed to update user:', error);
        } finally {
            hideGlobalLoader();
        }
    }

    const canChangeRole =
      authManager.isRootAdmin() ||
      authManager.hasPermission(PERMISSIONS.USERS_MANAGE, installation.id);

    return (
      <CardContainer>
          <div>
              <Helmet>
                  <title>{`${user.name} | User details`}</title>
              </Helmet>

              <Container spaceBelow="medium">
                  <FlashMessage locationState={location.state} />

                  <Header>
                      <Left>
                          <div>
                              <H1 spaceBelow="none">{user.name}</H1>

                              <Byline>
                                  <Role role={currentRole} />
                                  , user since{' '}
                                  {fmtDate(
                                    user.createdAt,
                                    installation.timeZoneName
                                  )}
                              </Byline>
                          </div>
                      </Left>

                      <Right>
                          <Status isEnabled={user.isEnabled} />
                      </Right>
                  </Header>

                  <hr style={{ color: '#B1B1B2' }} />
                  <br />
              </Container>

              <Container spaceAbove="medium" spaceBelow="medium">
                  <H2>User details</H2>

                  {showSuccessMessage && (
                    <Alert flavour="success">
                        Your changes have been saved.
                    </Alert>
                  )}

                  {submitError && <GraphqlErrorMessage error={submitError} />}

                  <UserDetailsForm
                    user={user}
                    roles={roles}
                    onUpdate={onUpdate}
                    onUpdatePassword={onUpdatePassword}
                    onCancel={onCancel}
                    canChangeRole={canChangeRole}
                    canChangeMfa={authManager.isRootAdmin()}
                  />
              </Container>
          </div>
      </CardContainer>
    );
}

export default withInstallation(UserDetails);
